type CoordinateValue = number | string | null | undefined;
type CoordinateLike = {
  lat?: CoordinateValue;
  lng?: CoordinateValue;
} | null | undefined;

export const PLANNED_ROUTE_OPTIONS = {
  strokeColor: "#185ce3",
  strokeOpacity: 0.9,
  strokeWeight: 3,
  zIndex: 2,
  geodesic: false,
};

export const DIVERSION_ROUTE_OPTIONS = {
  strokeColor: "#dc2626",
  strokeOpacity: 0.95,
  strokeWeight: 5,
  zIndex: 3,
  geodesic: false,
};

export const RECORDED_GPS_TRACK_OPTIONS = {
  strokeColor: "#FF9F45",
  strokeOpacity: 0.95,
  strokeWeight: 4,
  zIndex: 4,
  geodesic: false,
};

const isMissingCoordinate = (value: CoordinateValue) =>
  value === null || value === undefined || value === "";

const parseCoordinate = (value: CoordinateValue): number | null => {
  if (isMissingCoordinate(value)) {
    return null;
  }

  const stringValue =
    typeof value === "string" ? value.trim() : String(value);

  if (stringValue === "") {
    return null;
  }

  const parsed = Number(stringValue);

  return Number.isFinite(parsed) ? parsed : null;
};

export const toLatLng = (coordinates: CoordinateLike): Coordinates | null => {
  if (!coordinates) {
    return null;
  }

  const lat = parseCoordinate(coordinates.lat);
  const lng = parseCoordinate(coordinates.lng);

  if (lat === null || lng === null) {
    return null;
  }

  return { lat, lng };
};

export const buildWaypointPath = (waypoints: Waypoint[]): Coordinates[] =>
  waypoints.reduce<Coordinates[]>((acc, waypoint) => {
    const coordinates = toLatLng(
      waypoint.coordinates as unknown as {
        lat?: CoordinateValue;
        lng?: CoordinateValue;
      }
    );

    if (coordinates) {
      acc.push(coordinates);
    }

    return acc;
  }, []);

export const buildDiversionRoutePath = (waypoints: Waypoint[]): Coordinates[] =>
  waypoints.reduce<Coordinates[]>((acc, waypoint) => {
    if (`${waypoint["function"] || ""}`.trim().toUpperCase() !== "DIV") {
      return acc;
    }

    const coordinates = toLatLng(
      waypoint.coordinates as unknown as {
        lat?: CoordinateValue;
        lng?: CoordinateValue;
      }
    );

    if (coordinates) {
      acc.push(coordinates);
    }

    return acc;
  }, []);

export const buildRecordedGpsPath = (recordedGpsTrack: GPSPoint[]): Coordinates[] =>
  recordedGpsTrack.reduce<Coordinates[]>((acc, point) => {
    const latitude = point.latitude as unknown as CoordinateValue;
    const longitude = point.longitude as unknown as CoordinateValue;

    if (isMissingCoordinate(latitude) || isMissingCoordinate(longitude)) {
      return acc;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      acc.push({ lat, lng });
    }

    return acc;
  }, []);

export const buildGpsPath = buildRecordedGpsPath;

export const buildMapBoundsPoints = (
  plannedRoutePath: Coordinates[],
  recordedGpsPath: Coordinates[],
  waypointPath: Coordinates[],
  diversionRoutePath: Coordinates[] = []
): Coordinates[] => {
  const seen = new Set<string>();

  return [
    ...plannedRoutePath,
    ...diversionRoutePath,
    ...recordedGpsPath,
    ...waypointPath,
  ].filter((point) => {
    const key = `${point.lat}:${point.lng}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};
